import { MonetizationOnRounded, School } from "@mui/icons-material";
import { Avatar, Box, Card, CardContent, Chip, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import { Classroom } from "../../models/classroom";
import { memo } from "react";

interface ClassroomSelectionProps {
    classrooms: Record<number, Classroom>;
    selectedClassroomId: number;
    setSelectedClassroomId: React.Dispatch<React.SetStateAction<number>>;
}

function ClassroomSelection({ classrooms, selectedClassroomId, setSelectedClassroomId }: ClassroomSelectionProps) {
    return (
        <Card 
            elevation={0}
            sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backgroundColor: 'grey.50'
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar 
                            sx={{ 
                                bgcolor: 'primary.main',
                                width: 48,
                                height: 48,
                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                            }}
                        >
                            <MonetizationOnRounded sx={{ fontSize: 24 }} />
                        </Avatar>
                        
                        <Typography 
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: '#1e293b',
                                flexGrow: 1
                            }}
                        >
                            Payout
                        </Typography>
                    </Stack>

                    <FormControl fullWidth>
                        <Select
                            value={selectedClassroomId}
                            onChange={(e) => setSelectedClassroomId(e.target.value)}
                            displayEmpty
                            sx={{
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'divider',
                                    borderWidth: '1px'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                    borderWidth: '2px'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                    borderWidth: '2px'
                                },
                                '& .MuiSelect-select': {
                                    py: 1.5
                                }
                            }}
                        >
                            <MenuItem value={0} disabled>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    fontStyle="italic"
                                >
                                    Select a classroom to withdraw from...
                                </Typography>
                            </MenuItem>

                            {Object.values(classrooms).map((classroom) => (
                                <MenuItem 
                                    key={classroom.id} 
                                    value={classroom.id}
                                    sx={{
                                        py: 1.5,
                                        '&:hover': {
                                        bgcolor: 'primary.50'
                                        }
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2} width="100%">
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" fontWeight="500">
                                                {classroom.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Earnings: ₦{classroom.earnings.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default memo(ClassroomSelection);